import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { ChartBar, Files, PenNib, ChatCircleDots, SignOut, Spinner, WarningCircle } from "@phosphor-icons/react";
import { makePortalApi, getPortalSession, clearPortalSession, portalErr } from "@/lib/portalApi";
import PortalLogin from "./PortalLogin";
import PortalStatus from "./PortalStatus";
import PortalDokumente from "./PortalDokumente";
import PortalUnterschrift from "./PortalUnterschrift";
import PortalKontakt from "./PortalKontakt";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { key: "status", label: "Status", icon: ChartBar },
  { key: "dokumente", label: "Dokumente", icon: Files },
  { key: "unterschrift", label: "Unterschrift", icon: PenNib },
  { key: "kontakt", label: "Kontakt", icon: ChatCircleDots },
];

export default function Portal() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [infoError, setInfoError] = useState(null);
  const [loggedIn, setLoggedIn] = useState(!!getPortalSession(token));
  const [me, setMe] = useState(null);
  const [meLoading, setMeLoading] = useState(false);
  const [tab, setTab] = useState("status");

  const api = useMemo(() => makePortalApi(token, (detail) => {
    setLoggedIn(false); setMe(null);
    if (detail) toast.error(detail);
  }), [token]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/portal/public/${token}/info`);
        setInfo(r.data);
      } catch (e) {
        setInfoError(portalErr(e, "Dieser Zugangslink ist ungültig"));
      }
    })();
  }, [api, token]);

  const loadMe = useCallback(async (silent = false) => {
    if (!silent) setMeLoading(true);
    try {
      const r = await api.get("/portal/me");
      setMe(r.data);
    } catch (e) {
      if (e?.response?.status !== 401) toast.error(portalErr(e, "Daten konnten nicht geladen werden"));
    } finally { setMeLoading(false); }
  }, [api]);

  useEffect(() => { if (loggedIn) loadMe(); }, [loggedIn, loadMe]);

  const logout = () => { clearPortalSession(token); setLoggedIn(false); setMe(null); setTab("status"); };

  const logoUrl = info?.kanzlei?.has_logo ? `${BACKEND_URL}/api/portal/public/${token}/logo` : null;

  if (infoError) {
    return (
      <Shell>
        <div className="max-w-md mx-auto mt-16 bg-white rounded-2xl border border-[#E5E7EB] p-8 text-center shadow-sm" data-testid="portal-error">
          <WarningCircle size={40} className="mx-auto text-[#D32F2F] mb-3" weight="duotone" />
          <h1 className="font-serif text-3xl text-[#0A192F] mb-2">Zugang nicht möglich</h1>
          <p className="text-sm text-[#4B5563]">{infoError}</p>
          <p className="text-xs text-[#9CA3AF] mt-5">Bitte wenden Sie sich an Ihre Kanzlei, um einen neuen Zugangslink zu erhalten.</p>
        </div>
      </Shell>
    );
  }

  if (!info) {
    return (
      <Shell>
        <div className="flex items-center justify-center h-[60vh] text-[#6B7280] gap-3" data-testid="portal-loading">
          <Spinner size={22} className="animate-spin" /> Portal wird geladen …
        </div>
      </Shell>
    );
  }

  if (!loggedIn) {
    return (
      <Shell>
        <PortalLogin token={token} info={info} logoUrl={logoUrl} api={api} onSuccess={() => setLoggedIn(true)} />
      </Shell>
    );
  }

  const badges = {
    dokumente: me?.dokumente_anzahl || 0,
    unterschrift: me?.unterschrift?.offen || 0,
    kontakt: me?.nachrichten_ungelesen || 0,
  };

  return (
    <Shell>
      <header className="bg-[#1A2238] text-white" data-testid="portal-header">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            {logoUrl ? (
              <span className="bg-white rounded-lg px-2 py-1.5 shrink-0"><img src={logoUrl} alt="" className="h-7 max-w-[110px] object-contain" /></span>
            ) : null}
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.18em] text-white/55">Mandantenportal</p>
              <p className="font-serif text-lg leading-tight truncate" data-testid="portal-kanzlei-name">{info.kanzlei?.name || "Ihre Kanzlei"}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="hidden sm:inline-flex font-mono text-xs bg-white/10 rounded-md px-2.5 py-1" data-testid="portal-aktenzeichen">Az. {info.aktenzeichen}</span>
            <button onClick={logout} className="h-9 w-9 rounded-full hover:bg-white/10 flex items-center justify-center" title="Abmelden" data-testid="portal-logout">
              <SignOut size={18} />
            </button>
          </div>
        </div>
        {/* Desktop-Tabs */}
        <nav className="hidden md:block border-t border-white/10">
          <ul className="max-w-3xl mx-auto px-6 flex gap-1">
            {TABS.map(({ key, label, icon: Icon }) => (
              <li key={key}>
                <button
                  onClick={() => setTab(key)}
                  data-testid={`portal-tab-${key}`}
                  className={`relative flex items-center gap-2 px-4 py-3 text-sm transition-colors ${tab === key ? "text-white" : "text-white/60 hover:text-white"}`}
                >
                  <Icon size={18} weight={tab === key ? "fill" : "duotone"} /> {label}
                  {badges[key] > 0 && <Badge n={badges[key]} />}
                  {tab === key && <span className="absolute left-3 right-3 -bottom-px h-[2px] bg-[#D32F2F] rounded-full" />}
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-28 md:pb-12" data-testid={`portal-content-${tab}`}>
        {meLoading && !me ? (
          <div className="flex items-center justify-center h-[40vh] text-[#6B7280] gap-3"><Spinner size={22} className="animate-spin" /> Lädt …</div>
        ) : (
          <>
            {tab === "status" && <PortalStatus me={me} onGoto={setTab} />}
            {tab === "dokumente" && <PortalDokumente api={api} onChanged={() => loadMe(true)} />}
            {tab === "unterschrift" && <PortalUnterschrift api={api} onSigned={() => loadMe(true)} />}
            {tab === "kontakt" && <PortalKontakt api={api} me={me} onChanged={() => loadMe(true)} />}
          </>
        )}
      </main>

      {/* Mobile Bottom-Nav */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-lg border-t border-[#E5E7EB] shadow-[0_-2px_16px_rgba(10,25,47,0.10)]"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
        data-testid="portal-bottom-nav"
      >
        <ul className="grid grid-cols-4 h-16">
          {TABS.map(({ key, label, icon: Icon }) => {
            const active = tab === key;
            return (
              <li key={key}>
                <button
                  onClick={() => setTab(key)}
                  data-testid={`portal-nav-${key}`}
                  className={`w-full h-full flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-all active:scale-95 ${active ? "text-[#1A2238]" : "text-[#9CA3AF]"}`}
                >
                  <span className={`relative flex items-center justify-center ${active ? "scale-110" : ""} transition-transform`}>
                    {active && <span className="absolute -inset-1.5 rounded-full bg-[#1A2238]/8" aria-hidden />}
                    <Icon size={24} weight={active ? "fill" : "duotone"} className="relative" />
                    {badges[key] > 0 && <Badge n={badges[key]} floating />}
                  </span>
                  <span>{label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
    </Shell>
  );
}

function Badge({ n, floating }) {
  return (
    <span className={`${floating ? "absolute -top-1.5 -right-2.5" : "ml-1"} min-w-[18px] h-[18px] px-1 rounded-full bg-[#D32F2F] text-white text-[10px] font-bold flex items-center justify-center`} data-testid="portal-badge">
      {n > 9 ? "9+" : n}
    </span>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#F4F3EF] text-[#0A192F]" data-testid="portal-page">
      <Toaster position="top-center" richColors />
      {children}
    </div>
  );
}
